import type { CostCenterAllocation, FinanceTransaction } from '@/types/finance';

/** Allocation genérica: uma chave (categoria, centro de custo, ...) e um valor parcial. */
export interface KeyedAllocation {
  key: string;
  amount: number;
}

/**
 * Distribui o total efetivo (amount + interest + fine) proporcionalmente
 * entre as allocations, com base no peso de cada allocation em relação ao
 * `amount` original da transação.
 *
 * Exemplo: amount=R$5000, interest=R$50, fine=R$50, allocations [{a,3000},{b,2000}]
 * → total efetivo = R$5100. Ratio a = 3000/5000 = 0.6 → R$3060; b = 0.4 → R$2040.
 *
 * Caso amount=0 (defensivo): divide igual. Allocations vazio: retorna [].
 */
export function distributeTotalWithCharges(
  tx: Pick<FinanceTransaction, 'amount' | 'interest' | 'fine'>,
  allocations: KeyedAllocation[],
): KeyedAllocation[] {
  if (allocations.length === 0) return [];

  const interest = tx.interest ?? 0;
  const fine = tx.fine ?? 0;
  const totalIncluding = tx.amount + interest + fine;

  if (tx.amount === 0) {
    const equal = totalIncluding / allocations.length;
    return allocations.map((a) => ({ key: a.key, amount: equal }));
  }

  return allocations.map((a) => ({
    key: a.key,
    amount: (a.amount / tx.amount) * totalIncluding,
  }));
}

/**
 * Retorna allocations efetivas de centro de custo de uma transação.
 * - Com split (costCenterSplits.length > 0): cada split é uma allocation.
 * - Sem split, com costCenterId: 1 allocation virtual com o valor cheio.
 * - Sem centro de custo nem split: array vazio (o consumidor decide o destino).
 *
 * Espelho puro de FinanceContext.getCostCenterAllocations — mantemos cópia aqui
 * para os tests funcionarem sem dependência de React/contexto.
 */
export function getCostCenterAllocationsFor(tx: FinanceTransaction): CostCenterAllocation[] {
  if (tx.costCenterSplits && tx.costCenterSplits.length > 0) {
    return tx.costCenterSplits;
  }
  if (!tx.costCenterId) return [];
  return [{ costCenterId: tx.costCenterId, amount: tx.amount, orderIndex: 0 }];
}

/**
 * Distribui o total efetivo (amount + interest + fine) entre os centros de
 * custo da transação, proporcional ao peso de cada split.
 * Retorna [] quando a transação não tem centro de custo algum.
 */
export function distributeCostCentersWithCharges(
  tx: FinanceTransaction,
): { costCenterId: string; amount: number }[] {
  const allocations = getCostCenterAllocationsFor(tx);
  return distributeTotalWithCharges(
    tx,
    allocations.map((a) => ({ key: a.costCenterId, amount: a.amount })),
  ).map((p) => ({ costCenterId: p.key, amount: p.amount }));
}
